/**
 * `toolTurnHandover.ts` — which tool results END the model's turn, and which of
 * those come with a fixed line to say in its place.
 *
 * Some tools hand the SPOKEN turn to something other than the model, so the
 * transport must NOT ask for a follow-up reply after posting the result. Both
 * transports need the same answer — `RealtimeSession` (which would otherwise
 * send `response.create`) and `ResponsesTurnBridge` (which would otherwise run
 * another round) — so the rule lives here rather than being written out twice.
 *
 * Seven cases today, and they are the same shape:
 *
 *   - `present_options` — the learner answers with a button, and their picks
 *     come back as the next user message (Unity suppresses its follow-up too).
 *   - `go_to_workflow_step`, on ANY successful move. When the arrival's own
 *     on-start script is about to narrate, the author's lines are the narration
 *     for that step, and an acknowledgment either talks over them or, worse, is
 *     composed from the NEW step's freshly-pushed run-state bullets: "we're
 *     still on this step for a moment, the video hasn't finished…", spoken
 *     before the script has said a word. That was the first reported bug. When
 *     nothing on the step speaks, see the ⚠ below.
 *   - `start_workflow`, on ANY success. The run takes the session over in the
 *     middle of the call — playback switches the role to the Guide before the
 *     result is posted — so a follow-up would be the Guide answering a call it
 *     never made, over the first step's own `say` (two `response.create`s on one
 *     socket, and the second is dropped), while on the Responses bridge the
 *     switch has already reset the conversation the follow-up would continue. A
 *     start parked on console placement ends the turn too: the placement slate
 *     says why it is up.
 *   - `stop_workflow`, on ANY success — and this one's reply is REPLACED, not
 *     merely silenced. The result carries a fixed confirmation in a `reply="…"`
 *     field ("Preview stopped" for an author, "Workflow stopped" for a learner
 *     who is taken out of the project), and the orchestrator speaks exactly that
 *     line in the model's place (user direction, AI-1357: "a standard response
 *     string always"). Composed by the model, the confirmation changed wording
 *     from call to call and trailed a sentence about the editor, which is what
 *     that asked to remove. The line rides `INTERNAL SAY:`, the contract that
 *     already delivers an author's words verbatim.
 *   - `control_media`, on ANY success (user direction, 2026-09-17: "give the
 *     control_media a narration field and make the move or the replay silent").
 *     Working the learner's own video is not news — they asked for it, they are
 *     looking at the panel, and its scrubber shows where the clip landed. Left
 *     to answer, the model reported the action back ("Playing it again now."),
 *     which is a preamble in everything but POSITION: `base.md`'s ban is worded
 *     against the pre-call slot while `:58`/`:67` require one utterance AFTER
 *     the work, and "the result" is what an action's outcome reads as. Closing
 *     the slot at the transport is what makes it stick — the neighbouring
 *     `go_to_workflow_step` is silent for the same reason, and a prompt line
 *     asking for silence would be fighting a `gpt-realtime-2.x` default.
 *   - `Toggle_Model_Animation`, when it REPLAYED a take (`restart`, AI-1459) —
 *     the animation's counterpart of `control_media replay` (user direction,
 *     2026-10-05: "similar to how we handle video steps"), and silent for the
 *     same reason. Measured live before this: the Guide replayed the take and
 *     then said "The animation is replaying from the beginning." with the
 *     prompt already asking for a silent turn. Every other result of the tool,
 *     a settings change included, keeps its voice.
 *   - `Use_ConstantListening`, on ANY success, with a fixed line like
 *     `stop_workflow`'s: "Done.", either way. User direction, 2026-10-08: a
 *     hum or a stray word on the open mic must not get a reply about
 *     listening. Composed by the model, the confirmation promised to keep
 *     listening ("You can just speak, and I'll pick it up automatically"), and
 *     the model gave that promise back as its reply to the next sound:
 *     "Continuous listening is on, so I'll wait for your next instruction" (3
 *     of 3 noise turns in the reporting trace). Replaying that trace's
 *     conversation, a synthetic hum drew a listening-mode reply 10 of 62 times
 *     with the composed confirmation and 0 of 40 with "Done.", while four
 *     prompt rules against mentioning the mode did not move it (each 1–3 of 8,
 *     one making it worse). The model echoes its own earlier line over a rule;
 *     taking the line out of its hands is what held, and the line itself must
 *     not name the mode either (see `K_LISTENING_SWITCHED_REPLY`).
 *
 * And ONE refusal ends the turn too: a step move refused because the learner's
 * turn was only sound (`move_not_requested` stamped `narration=none`, AI-1412).
 * Nobody asked anything, so there is nothing to answer. `roles/guide.md`
 * already calls such a turn silent, and a spoken refusal there is Spark talking
 * back to background noise. The executor decides (only it holds the guardrail's
 * verdict) and stamps the result; this module only reads it. And two refusals
 * end it with a FIXED line instead of silence: "nothing to replay here" for a
 * video or an animation (`K_SPOKEN_REPLY_TOOLS`). Every other refusal keeps its
 * voice.
 *
 * ⚠ The STEP-MOVE case used to be CONDITIONAL, and that is reversed (user
 * direction, 2026-09-28). A step with no speaking on-start script — a
 * full-size image step, typically — returned `narration=you`, the role told the
 * model to "narrate the step", and it did: in its own words, with checks the
 * workflow never asked for ("make sure the front cover is removed and the area
 * looks clear"). That reply was an ordinary untagged response, so it also
 * showed in the chat and was saved to the transcript as the answer to "next",
 * which AI-1395 forbids for step narration. A silent move is what the learner's
 * own Next button gives on the same step, so the agent's move now matches it:
 * the executor still says WHICH case it is (`narration=script` or
 * `narration=none`, because the model reads that too), and both end the turn.
 * Moves that FINISH the training (`run_finished`, `workflow_complete`) carry no
 * narration field and keep their voice, because "you're done" is a real answer.
 * The START case is unconditional for the same reason as the moves: a run whose
 * first step says nothing gets no agent speech from the Play button either, and
 * matching the button is the whole contract of the tool.
 */

import { AgentToolNames } from "@ai/registry/agentToolNames";

/**
 * The `narration=` field `go_to_workflow_step` stamps on a successful move.
 *
 * A field on the existing `ok: workflow_step …` line rather than a side channel,
 * because it is worth saying to the MODEL as well as to the transport: it is
 * what tells the agent whether the step it just moved to narrates itself. One
 * constant, imported by the producer and by this reader, so the two cannot
 * drift into a silent no-op.
 */
export const K_NARRATION_SCRIPT = "narration=script";

/**
 * The other value, and it means NOBODY speaks: stamped by `control_media` on a
 * successful command, by `Toggle_Model_Animation` on a successful replay, and
 * by `go_to_workflow_step` on a move to a step with no speaking on-start
 * script.
 *
 * Same field as the one above because it answers the same question — who has
 * the floor after this call — and the model reads it as well as the transport.
 * For `control_media` the video itself is the outcome; for a step move the
 * step's own text on the learner's panel is. Either way there is no line to
 * compose and the turn simply ends. (A third value, `narration=you`, told the
 * model to narrate a script-less step itself; it was retired 2026-09-28 — see
 * the header.)
 *
 * ⚠ It is stamped on `ok:` only, with ONE exception. Every other refusal returns
 * an `error:` line, which `toolHandedOverSpokenTurn` rejects — so
 * `media_not_ready`, `step_not_ready` and the rest keep their voice. Silence
 * there would read as a hang, and they have no other surface. The exception is
 * {@link K_MOVE_NOT_REQUESTED} on a turn that was only sound (AI-1412): nobody
 * asked anything, so there is nothing to answer — see the header.
 */
export const K_NARRATION_NONE = "narration=none";

/**
 * The code `go_to_workflow_step` refuses with when the learner's last turn did
 * not ask for the move (AI-1412, `AIGuardrailsModel.StepMove.ts`). Shared by
 * the producer and {@link toolHandedOverSpokenTurn}, like the `narration=`
 * constants.
 */
export const K_MOVE_NOT_REQUESTED = "move_not_requested";

/**
 * The start of the one refusal that ends the turn. Anchored at the start of the
 * result, like {@link K_SPOKEN_REPLY_FIELD}, so the learner's own words quoted
 * later in the line can never match it.
 */
const K_SILENT_MOVE_REFUSAL = `error: ${K_MOVE_NOT_REQUESTED} ${K_NARRATION_NONE} `;

/**
 * The field a tool stamps DIRECTLY after its status word when its outcome has
 * ONE fixed confirmation to speak: `ok: workflow_stopped reply="Preview stopped" …`.
 *
 * The producer and the reader share this function and the pattern below, the
 * way `narration=` shares its constants. The field sits right after the status
 * because the rest of a result is free text — a workflow or step name quoted
 * later in the line could contain anything, and the reader is anchored so that
 * it never matches inside one.
 */
export function formatToolSpokenReply(line: string): string {
  return ` reply="${line}"`;
}

const K_SPOKEN_REPLY_FIELD = /^(?:ok|error): \S+ reply="([^"]+)"/;

/**
 * The tools that stamp a fixed line, and the only ones read for one, so a
 * `reply="` that happens to appear in some other tool's output is never
 * mistaken for one.
 *
 * - `stop_workflow`, on success (AI-1357, see the header).
 * - `control_media` and `Toggle_Model_Animation`, when there is nothing on the
 *   step to replay (user direction, 2026-10-05: "it should say 'There are no
 *   animations to replay here'", and "there are no videos to replay here" for
 *   the video). Left to answer, the Guide asked the learner which model to
 *   replay on a step that showed a video and no model. A REFUSAL with a fixed
 *   line, the one kind that is spoken by Spark rather than by the model — every
 *   other refusal keeps the model's voice.
 * - `Use_ConstantListening`, on success: "Done." (2026-10-08, see the header).
 */
const K_SPOKEN_REPLY_TOOLS: ReadonlySet<string> = new Set([
  AgentToolNames.StopWorkflow,
  AgentToolNames.ControlMedia,
  AgentToolNames.ToggleModelAnimation,
  AgentToolNames.UseConstantListening,
]);

/**
 * The exact line to SPEAK in place of the model's reply to this tool call, or
 * null when the model answers as usual. A non-null answer also ends the turn
 * ({@link toolHandedOverSpokenTurn}).
 */
export function toolSpokenReply(toolName: string, result: string): string | null {
  if (!K_SPOKEN_REPLY_TOOLS.has(toolName)) return null;
  const line = K_SPOKEN_REPLY_FIELD.exec(result)?.[1]?.trim() ?? "";
  return line.length > 0 ? line : null;
}

/**
 * True when the tool call that just ran handed the spoken turn away, so the
 * caller must not ask the model to reply.
 *
 * `result` is matched rather than a separate flag because it is the channel
 * both transports already have — neither holds the executor, and neither may
 * know what a workflow is.
 */
export function toolHandedOverSpokenTurn(toolName: string, result: string): boolean {
  if (toolName === AgentToolNames.GoToWorkflowStep && result.startsWith(K_SILENT_MOVE_REFUSAL)) {
    return true;
  }
  // Before the `ok` gate: a fixed line can ride a refusal ("nothing to replay
  // here"), and Spark speaks it in the model's place either way.
  if (toolSpokenReply(toolName, result) !== null) return true;
  if (!result.startsWith("ok")) return false;
  if (toolName === AgentToolNames.PresentOptions) return true;
  if (toolName === AgentToolNames.GoToWorkflowStep) {
    return result.includes(K_NARRATION_SCRIPT) || result.includes(K_NARRATION_NONE);
  }
  if (toolName === AgentToolNames.ControlMedia) return result.includes(K_NARRATION_NONE);
  if (toolName === AgentToolNames.ToggleModelAnimation) return result.includes(K_NARRATION_NONE);
  if (toolName === AgentToolNames.StartWorkflow) return true;
  return false;
}
